import React, { useEffect, useLayoutEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { setMobileSidebarKill, setMobileSidebarStatus } from "../../../redux/slices/layoutSlice";
import ProgressBar from "@ramonak/react-progress-bar";
import { ReactComponent as Cloud } from "../../../assets/images/sidebar/Cloud.svg";
import { ReactComponent as Close } from "../../../assets/images/sidebar/Times.svg";
import { ReactComponent as Logout } from "../../../assets/images/sidebar/Logout.svg";
import { ReactComponent as ShoppingCart } from "../../../assets/images/icons/ShoppingCart.svg";
import emptyProfile from "../../../assets/images/sidebar/EmptyProfile.svg";
import { setUser } from "../../../redux/slices/authSlice";
import Button from "../../../utils/component/button/button.index";
import { ButtonSize, ButtonType } from "../../../utils/component/button/button.interface";
import SidebarMenuItems from "./sidebarMenuItems/sidebarMenuItems.index";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import { authToken } from "../../../utils/storage/storage";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { convertByteToGb, getWebEnvConfig, isTouchScreen } from "../../../scripts/helpers";
import { post, responseValidator } from "../../../scripts/api";
import API from "../../../urls/api";
import PATH from "../../../urls/path";
import { DeviceEnum } from "../../../redux/slices/environmentSlice";
import { handleClearAllSelectedResources, handleClearDefaultSelectedItem } from "../../../utils/component/fileManagers/fileManager.actions";
import { zIndexs } from "../../../interfaces/environment";
import store from "../../../redux/store";
import { UserPlanStatus } from "../../../interfaces/general";
import { ENVIRONMENT } from "../../../environment";
import LogoWithName from "../../../utils/component/logo/logoWithName.index";
import LogoWithoutName from "../../../utils/component/logo/logoWithoutName.index";
import { shallowEqual } from "react-redux";

export default function Sidebar(): JSX.Element {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAppSelector(({ auth: { user } }) => ({ user }), shallowEqual);
    const { mobileSidebarKill, mobileSidebarStatus } = useAppSelector(
        ({ layout: { mobileSidebarKill, mobileSidebarStatus } }) => ({ mobileSidebarKill, mobileSidebarStatus }),
        shallowEqual
    );
    const { activePurchases, quota } = useAppSelector(({ fileManager: { activePurchases, quota } }) => ({ activePurchases, quota }), shallowEqual);
    const { device } = useAppSelector(({ environment: { device } }) => ({ device }), shallowEqual);

    useEffect(() => {
        if (device === DeviceEnum.mobile) dispatch(setMobileSidebarKill(true));
    }, [location.pathname]);

    useEffect(() => {
        if (activePurchases) {
            const element = document.querySelectorAll(".CircularProgressbar .CircularProgressbar-path");
            if (element && element[element.length - 1]) {
                element[element.length - 1].classList.add(
                    activePurchases.userPlanStatus === UserPlanStatus.NORMAL ? "normal-circularProgressbar" : "danger-circularProgressbar"
                );
            }
        }
    }, [activePurchases]);

    const handleLogout = () => {
        if (device === DeviceEnum.mobile) dispatch(setMobileSidebarKill(true));
        return new Promise((resolve) => {
            post(
                (data, status) => {
                    if (responseValidator(status)) {
                        dispatch(setUser(null));
                        authToken.remove();
                        window.location.replace(`${getWebEnvConfig("landingBaseUrl")}auth/login`);
                    }
                    resolve(true);
                },
                API.auth.logout(),
                { refresh: authToken.get()?.refresh }
            );
        });
    };

    useEffect(() => {
        if (mobileSidebarKill) {
            setTimeout(() => {
                dispatch(setMobileSidebarStatus(false));
                dispatch(setMobileSidebarKill(false));
            }, 500);
        }
    }, [mobileSidebarKill]);

    useLayoutEffect(() => {
        const els = document.querySelectorAll(".CircularProgressbar-path");
        els.forEach((e) => {
            if (e && activePurchases)
                e.classList.add(
                    activePurchases.userPlanStatus === UserPlanStatus.NORMAL ? "normal-circularProgressbar" : "danger-circularProgressbar"
                );
        });
    }, [activePurchases]);

    return (
        <React.Fragment>
            {mobileSidebarStatus && (
                <div
                    onClick={() => dispatch(setMobileSidebarKill(true))}
                    className={`sidebar-wrapper w-screen h-full bg-overlay fixed top-0 left-0 animate__animated animate__faster ${mobileSidebarKill ? "animate__fadeOut" : "animate__fadeIn"}`}
                    style={{ zIndex: zIndexs.sidebarModal }}
                />
            )}
            <div
                onClick={() => {
                    handleClearAllSelectedResources();
                    handleClearDefaultSelectedItem();
                }}
                className={`sidebar-wrapper overflow-auto no-scrollbar w-full bg-white flex flex-col ${isTouchScreen() ? `rounded-tl-[40px] rounded-bl-[40px] h-full max-w-[330px] fixed top-0 right-0 z-[1000000] ${mobileSidebarKill ? "animate__slideOutRight" : "animate__slideInRight"} ${!mobileSidebarStatus && "!hidden"}` : "lg:max-w-[240px] hidden sm:block relative max-w-[80px] rounded-[20px]"} animate__animated animate__faster`}
                id="sidebar"
            >
                <div className={`${isTouchScreen() ? "p-8" : "lg:p-8 sm:p-4 sm:pt-8"}`}>
                    <div
                        className={`flex items-center justify-between ${isTouchScreen() ? "justify-between" : "lg:justify-between sm:justify-center"}`}
                    >
                        {isTouchScreen() ? (
                            <div>
                                <LogoWithName />
                            </div>
                        ) : (
                            <Link to={PATH.dashboard}>
                                <div className="lg:block sm:hidden">
                                    <LogoWithName />
                                </div>
                                <div className="hidden lg:hidden sm:block">
                                    <LogoWithoutName />
                                </div>
                            </Link>
                        )}
                        {isTouchScreen() && (
                            <Close className="stroke-[#A0A3BD] cursor-pointer block" onClick={() => dispatch(setMobileSidebarKill(true))} />
                        )}
                    </div>
                </div>
                <div className="overflow-auto sidebar-overflow">
                    {isTouchScreen() && (
                        <div className="p-8 pt-0">
                            <div className="flex items-center gap-4">
                                <img
                                    className="block w-[85px] h-[85px] rounded-[30px] object-cover"
                                    src={user?.picture_url ? user.picture_url : emptyProfile}
                                    alt={user?.name}
                                    loading="lazy"
                                />
                                <div>
                                    <h4 className="text-secondary-800 whitespace-nowrap overflow-hidden text-ellipsis w-[180px]">
                                        {user?.name ? user.name : `کاربر ${ENVIRONMENT.brandName}`}
                                    </h4>
                                    <span className="text-secondary-600 text-[14px]">{user?.phone}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <SidebarMenuItems />
                    <>
                        <div className="px-4">
                            <hr />
                        </div>
                        <div
                            className={`${store.getState().newLogin.newLogin ? "flex flex-col justify-center md:w-full w-[90%]" : ""} py-4 memory-show`}
                        >
                            {activePurchases && (
                                <div
                                    className={`flex items-center gap-3 hover:shadow-lg ${!isTouchScreen() && "sm:justify-center lg:justify-start"} cursor-pointer py-2 px-8 lg:px-8 sm:px-4 text-secondary-800 w-[90%] rounded-tl-[30px] rounded-bl-[30px] btn-gradient`}
                                    onClick={() => navigate(PATH.duplicatedFiles)}
                                >
                                    <Cloud className={`fill-white`} />
                                    <span className={`flex-1 text-white text-[15px] font-bold ${!isTouchScreen() && "sm:hidden lg:block"}`}>
                                        حافظه
                                    </span>
                                </div>
                            )}
                            {activePurchases && quota && (
                                <>
                                    {!isTouchScreen() && (
                                        <div className="flex items-center justify-center">
                                            <div className="hidden sm:block lg:hidden mt-3">
                                                <CircularProgressbarWithChildren
                                                    value={
                                                        activePurchases.userPlanStatus === UserPlanStatus.DANGER
                                                            ? 100
                                                            : (100 * quota.used) / (quota.total_plan + quota.total_gift)
                                                    }
                                                    strokeWidth={10}
                                                    className="w-[40px]"
                                                >
                                                    <div className="flex flex-col items-center text-[8px] font-bold text-secondary-600 mt-[2px]">
                                                        <span className="text-[12px] font-bold">{`${activePurchases.userPlanStatus === UserPlanStatus.DANGER ? 100 : Math.ceil(Number(convertByteToGb(quota.used)) / Number(convertByteToGb(quota.total_plan + quota.total_gift)))}%`}</span>
                                                    </div>
                                                </CircularProgressbarWithChildren>
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        className={`${!isTouchScreen() && "sm:hidden lg:block"} ${store.getState().newLogin.newLogin ? "mt-4 pr-8 pl-2 lg:pr-8 lg:pl-8 sm:pl-4 sm:pr-4" : "mt-4 px-8 lg:px-8 sm:px-4"}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-secondary-400">
                                                {activePurchases.userPlanStatus === UserPlanStatus.DANGER
                                                    ? 100
                                                    : ((100 * quota.used) / (quota.total_plan + quota.total_gift)).toFixed(1)}
                                                %
                                            </span>
                                        </div>
                                        <ProgressBar
                                            completed={
                                                activePurchases.userPlanStatus === UserPlanStatus.NORMAL
                                                    ? (100 * quota.used) / (quota.total_plan + quota.total_gift)
                                                    : 100
                                            }
                                            labelSize={"0px"}
                                            dir="rtl"
                                            height={"8px"}
                                            bgColor={`linear-gradient(90deg, ${activePurchases.userPlanStatus === UserPlanStatus.NORMAL ? "#006ADA 0.01%, rgba(67, 230, 240, 0.49) 98.34%" : "rgba(226, 0, 0, 0.63) 0%, rgba(246, 135, 5, 0.965625) 53.12%, rgba(255, 230, 0, 0.96) 100%"})`}
                                        />
                                        <div className="text-secondary-800 text-[10px] mt-4">
                                            <span className="text-[14px] font-bold">{convertByteToGb(quota.used)}</span> از{" "}
                                            <span className="text-[14px] font-bold">
                                                {" "}
                                                {Number(convertByteToGb(quota.total_plan + quota.total_gift)).toFixed(0)}{" "}
                                            </span>{" "}
                                            گیگابایت استفاده شده
                                        </div>
                                    </div>
                                    {!isTouchScreen() && (
                                        <div className="hidden sm:block lg:hidden text-center text-[12px] mt-2 text-secondary-600 px-8 lg:px-8 sm:px-4">
                                            <span className="font-bold text-[14px]"> {convertByteToGb(quota.used)}</span>/
                                            <span className="font-bold text-[10px]">
                                                {Number(convertByteToGb(quota.total_plan + quota.total_gift)).toFixed(0)}
                                            </span>
                                            <span className="block text-[8px] mt-2">گیگابایت استفاده شده</span>
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="px-8 lg:px-8 sm:px-4">
                                <Button
                                    text="خرید فضای ابری"
                                    onClick={() => navigate(PATH.planRecommendation)}
                                    type={ButtonType.SECONDARY}
                                    size={ButtonSize.SM}
                                    className={`disable text-white hover:text-white border-none btn-gradient w-full mt-4 flex ${!isTouchScreen() && "sm:hidden lg:flex"}`}
                                    disable={false}
                                    autoFocus={false}
                                />
                                {!isTouchScreen() && (
                                    <Link
                                        to={PATH.planRecommendation}
                                        className="w-full btn-gradient mt-4 flex-col items-center justify-center p-1 rounded-[10px] cursor-pointer hidden sm:flex lg:hidden"
                                    >
                                        <ShoppingCart />
                                        <span className="text-bold text-white text-[12px] mt-1">فضای ابری</span>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </>

                    {isTouchScreen() && (
                        <>
                            <div className="px-8 mb-4">
                                <hr />
                            </div>
                            <div className="flex items-center gap-4 h-[50px] pb-4 cursor-pointer" onClick={handleLogout}>
                                <div className="w-full h-full mx-4 px-4 rounded-[10px] flex items-center gap-4 transition">
                                    <Logout />
                                    <div className="text-red-500 text-[15px]">خروج از حساب کاربری</div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
}
